import { Component } from '@theme/component';
import { preloadImage } from '@theme/utilities';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());
  #activeIndex = 0;
  #previousButton = /** @type {HTMLButtonElement | null} */ (null);
  #nextButton = /** @type {HTMLButtonElement | null} */ (null);

  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#previousButton?.removeEventListener('click', this.#showPrevious);
    this.#nextButton?.removeEventListener('click', this.#showNext);
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  open(index, event) {
    event.preventDefault();

    const { dialog } = this.refs;

    if (dialog.open) return;

    this.#createNavigation();
    this.#selectMedia(index, { load: false });

    // Horizon gives zoom dialogs smooth scrolling globally. Reset the stored
    // position while the dialog is still closed so opening it cannot animate
    // from a previous scroll offset.
    dialog.scrollTop = 0;
    dialog.scrollLeft = 0;
    dialog.tabIndex = -1;
    dialog.autofocus = true;
    dialog.showModal();
    dialog.focus({ preventScroll: true });

    const activeMedia = this.refs.media[this.#activeIndex];
    if (activeMedia) this.loadHighResolutionImage(activeMedia);
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    // Keep the rendered responsive image in the DOM. Replacing it after the
    // dialog opens changes its intrinsic dimensions and can trigger scroll
    // anchoring, which makes the page appear to flash or jump downward.
    this.#highResImagesLoaded.add(highResolutionUrl);
    preloadImage(highResolutionUrl);
    return true;
  }

  #createNavigation() {
    const { dialog, media } = this.refs;
    if (media.length <= 1 || dialog.querySelector('.dialog-zoomed-gallery__nav')) return;

    this.#previousButton = this.#createNavigationButton('previous', 'Previous image');
    this.#nextButton = this.#createNavigationButton('next', 'Next image');

    this.#previousButton.addEventListener('click', this.#showPrevious);
    this.#nextButton.addEventListener('click', this.#showNext);

    dialog.append(this.#previousButton, this.#nextButton);
  }

  /**
   * @param {'previous' | 'next'} direction
   * @param {string} label
   * @returns {HTMLButtonElement}
   */
  #createNavigationButton(direction, label) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `dialog-zoomed-gallery__nav dialog-zoomed-gallery__nav--${direction}`;
    button.setAttribute('aria-label', label);
    button.innerHTML = `
      <span class="dialog-zoomed-gallery__nav-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="${direction === 'previous' ? 'M15 5L8 12L15 19' : 'M9 5L16 12L9 19'}" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </span>
    `;
    return button;
  }

  #showPrevious = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.#selectMedia(this.#activeIndex - 1);
  };

  #showNext = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.#selectMedia(this.#activeIndex + 1);
  };

  /**
   * @param {number} index
   * @param {{ load?: boolean }} options
   */
  #selectMedia(index, options = {}) {
    const { media, thumbnails } = this.refs;
    if (!media.length) return;

    const { load = true } = options;

    const nextIndex = Math.max(0, Math.min(Number(index) || 0, media.length - 1));
    this.#activeIndex = nextIndex;

    media.forEach((item, itemIndex) => {
      const isActive = itemIndex === nextIndex;
      item.hidden = !isActive;
      item.toggleAttribute('data-zc-active', isActive);
      item.setAttribute('aria-hidden', String(!isActive));
    });

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, buttonIndex) => {
      button.setAttribute('aria-selected', String(buttonIndex === nextIndex));
    });

    if (this.#previousButton) this.#previousButton.disabled = nextIndex === 0;
    if (this.#nextButton) this.#nextButton.disabled = nextIndex === media.length - 1;

    const activeMedia = media[nextIndex];
    if (activeMedia && load) this.loadHighResolutionImage(activeMedia);

  }

  /**
   * Closes the zoom dialog.
   */
  close() {
    this.closeDialog();
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.#selectMedia(this.#activeIndex - 1);
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.#selectMedia(this.#activeIndex + 1);
    }
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  handleThumbnailClick(index) {
    this.#selectMedia(index);
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  selectThumbnail(index) {
    this.#selectMedia(index);
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}
